import { motion } from 'framer-motion';

export default function About() {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-montserrat font-bold text-4xl md:text-5xl text-text-dark mb-6">
              Locally Owned.<br />Quality First.
            </h2>
            <p className="font-inter text-lg text-text-muted mb-6 leading-relaxed">
              At KC PDR, we specialize in restoring your vehicle's appearance the right way — without paint, fillers, or unnecessary bodywork. Using advanced paintless dent repair techniques, our experts carefully remove dents, dings, and hail damage while preserving your car's original factory finish.
            </p>
            <p className="font-inter text-lg text-text-muted mb-8 leading-relaxed">
              With over 30 years of experience serving the Kansas City metro area, we're committed to honest pricing, fast turnaround, and exceptional craftsmanship. No hidden fees. No surprises. Just quality work you can trust.
            </p>

            {/* Why PDR Section */}
            <div className="bg-bg-soft rounded-2xl p-8 mb-8">
              <h3 className="font-montserrat font-bold text-2xl text-text-dark mb-4">
                Why Paintless Dent Repair?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-primary mt-1"></i>
                  <span className="font-inter text-text-muted">
                    <strong className="text-text-dark">No paint or fillers</strong> — maintains your vehicle's original finish
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-primary mt-1"></i>
                  <span className="font-inter text-text-muted">
                    <strong className="text-text-dark">Faster than body shops</strong> — most repairs done in hours, not days
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-primary mt-1"></i>
                  <span className="font-inter text-text-muted">
                    <strong className="text-text-dark">Preserves resale value</strong> — no paint work means no depreciation
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl text-primary mt-1"></i>
                  <span className="font-inter text-text-muted">
                    <strong className="text-text-dark">Insurance-friendly</strong> — we work with all major carriers
                  </span>
                </li>
              </ul>
            </div>

            {/* What to Expect */}
            <div>
              <h3 className="font-montserrat font-bold text-2xl text-text-dark mb-4">
                What to Expect
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-secondary/10 rounded-full flex-shrink-0">
                    <i className="ri-user-smile-line text-xl text-secondary"></i>
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-lg text-text-dark mb-1">
                      Honest Communication
                    </h4>
                    <p className="font-inter text-text-muted">
                      Clear estimates, honest advice, and no hidden fees—ever.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-full flex-shrink-0">
                    <i className="ri-timer-flash-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-lg text-text-dark mb-1">
                      Fast Service
                    </h4>
                    <p className="font-inter text-text-muted">
                      We value your time and get your vehicle back on the road quickly.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-secondary/10 rounded-full flex-shrink-0">
                    <i className="ri-tools-line text-xl text-secondary"></i>
                  </div>
                  <div>
                    <h4 className="font-montserrat font-semibold text-lg text-text-dark mb-1">
                      Expert Craftsmanship
                    </h4>
                    <p className="font-inter text-text-muted">
                      Every repair is done with precision and care to restore perfection.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.wixstatic.com/media/35bb17_ccd97b5da35b4bb5a57765252935924b~mv2.jpg/v1/crop/x_0,y_31,w_715,h_891/fill/w_455,h_567,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/20200205_104806.jpg"
                alt="Professional dent repair technician at work"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Stats Overlay */}
            <div className="absolute -bottom-8 -left-8 bg-primary text-white rounded-2xl p-6 shadow-xl">
              <div className="font-montserrat font-bold text-4xl mb-1">30+</div>
              <div className="font-inter text-sm">Years Experience</div>
            </div>
          </motion.div>
        </div>

        {/* Insurance Partners */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24"
        >
          <h3 className="font-montserrat font-bold text-3xl text-text-dark text-center mb-12">
            We Work With Major Insurance Companies
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 items-center">
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_d42b835b41614239abdc684dffec7cac~mv2.png/v1/fill/w_218,h_145,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/images%20(1).png"
                alt="Insurance partner"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_897a6b3d1b2b4f9a9c6e5624195aa404~mv2.png/v1/fill/w_218,h_145,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/allstate.png"
                alt="Allstate"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_a5438d85d9a34bb2a8b18004848151bd~mv2.jpg/v1/fill/w_218,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/348s.jpg"
                alt="Insurance partner"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_25c27ad875814dde930f023fd49d70f1~mv2.jpg/v1/fill/w_218,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/State_Farm_logo-2-_E61383C3-5056-A36A-0B5856B9484F3F14-e61382735056a36_e6138a79-5056-a36a-.jpg"
                alt="State Farm"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_523e3b1b7ab74c479b24257db0a5f7c8~mv2.jpg/v1/fill/w_218,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/12-10-24-1170x780_aa09bdba6fb08050ae6846e5f249f009112e1b1ed44d46d5f477d7705a7522cb.jpg"
                alt="Insurance partner"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_d97ba3bbb81d48358e73e669b6580ea8~mv2.jpg/v1/fill/w_218,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/a81a5f9a2da53b2e968bb91af769079d_edited_.jpg"
                alt="Insurance partner"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_616e010dd1d1430f99c877f6dbd8dea9~mv2.png/v1/fill/w_218,h_145,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/images.png"
                alt="Insurance partner"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
            <div className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 cursor-pointer">
              <img
                src="https://static.wixstatic.com/media/35bb17_6fcc79afe73445eeb33a4657921169e4~mv2.avif/v1/fill/w_218,h_145,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Farmers_Ins.avif"
                alt="Farmers Insurance"
                className="w-full h-auto max-h-16 object-contain"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
